import { useState, useEffect } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const automationService = {
  _url: process.env.REACT_APP_AUTOMATION_API_URL,

  // Workflow endpoints
  async getAllWorkflows() {
    let res = await Fetch.get(`${this._url}/workflows`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkflow(id) {
    let res = await Fetch.get(`${this._url}/workflows/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async createWorkflow(payload) {
    let res = await Fetch.post(`${this._url}/workflows`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async updateWorkflow(id, payload) {
    let res = await Fetch.put(`${this._url}/workflows/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async deleteWorkflow(id) {
    let res = await Fetch.delete(`${this._url}/workflows/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async duplicateWorkflow(id) {
    let res = await Fetch.post(`${this._url}/workflows/${id}/duplicate`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async executeWorkflow(id, payload) {
    let res = await Fetch.post(`${this._url}/workflows/${id}/execute`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async pauseWorkflow(id) {
    let res = await Fetch.post(`${this._url}/workflows/${id}/pause`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async resumeWorkflow(id) {
    let res = await Fetch.post(`${this._url}/workflows/${id}/resume`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkflowStats(id) {
    let res = await Fetch.get(`${this._url}/workflows/${id}/stats`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkflowAnalytics(id) {
    let res = await Fetch.get(`${this._url}/workflows/${id}/analytics`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsToWorkflow(id, payload) {
    let res = await Fetch.post(`${this._url}/workflows/${id}/add-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkflowExecutions(id, params = {}) {
    const query = new URLSearchParams(params).toString();
    let res = await Fetch.get(`${this._url}/workflows/${id}/executions${query ? '?' + query : ''}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  // Queue / rate-limit endpoints
  async getQueueStatus() {
    let res = await Fetch.get(`${this._url}/queue/status`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getProfileRateLimits(profileId) {
    let res = await Fetch.get(`${this._url}/queue/profile/${profileId}/rate-limits`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async updateProfileRateLimits(profileId, { limits, delays } = {}) {
    let res = await Fetch.put(`${this._url}/queue/profile/${profileId}/rate-limits`, { limits, delays });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkflowPendingApprovals({ page = 1, itemsPerPage = 10, campaignId = '' } = {}) {
    const qs = `page=${page}&itemsPerPage=${itemsPerPage}&campaignId=${campaignId || ''}`;
    let res = await Fetch.get(`${this._url}/workflows/pending-approvals?${qs}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  GetWorkflowPendingApprovals(searchQuery, fetch) {
    const [pendingApprovals, setPendingApprovals] = useState({ data: { items: [], totalItems: 0 } });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);

    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getWorkflowPendingApprovals(searchQuery))
        .then(res => {
          setPendingApprovals(res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      workflow_pending_loading: status === STATUS.LOADING,
      workflow_pending_error: status === STATUS.ERROR,
      workflow_pending_data: pendingApprovals,
    };
  },

  async approveAllWorkflowPendingApprovals(payload = {}) {
    let res = await Fetch.patch(`${this._url}/workflows/pending-approvals/approve-all`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async approveWorkflowPendingApproval(executionId, payload = {}) {
    let res = await Fetch.patch(
      `${this._url}/workflows/pending-approvals/executions/${executionId}/approve`,
      payload,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async updateWorkflowPendingApproval(executionId, payload) {
    let res = await Fetch.patch(
      `${this._url}/workflows/pending-approvals/executions/${executionId}`,
      payload,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async regenerateWorkflowPendingApproval(executionId) {
    let res = await Fetch.post(
      `${this._url}/workflows/pending-approvals/executions/${executionId}/regenerate`,
      {},
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async dismissWorkflowPendingApproval(executionId, payload) {
    let res = await Fetch.delete(
      `${this._url}/workflows/pending-approvals/executions/${executionId}`,
      payload,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};

export default automationService;
